import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiShare2, FiBookmark, FiThumbsUp, FiX, FiCopy } from 'react-icons/fi';
import { FaFacebook, FaTwitter, FaWhatsapp, FaLinkedin, FaEnvelope } from 'react-icons/fa';
import SEO from '../components/SEO';

const Watch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        console.log("Fetching video with ID:", id);
        const response = await fetch(`${API_BASE_URL}/video/${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch video');
        }
        const result = await response.json();
        console.log("Video data:", result);

        if (result.error || !result.data) {
          throw new Error('Invalid video data');
        }

        setVideo(result.data);
      } catch (err) {
        console.error("Error fetching video:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-dark text-white">
        <div className="text-center">
          <div className="text-xl mb-4">Loading video...</div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-dark text-white">
        <div className="text-center">
          <div className="text-xl mb-4">Error loading video</div>
          <p className="text-gray-400 mb-6">{error || 'Video not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-gradient-accent text-white rounded-md hover:opacity-90 transition-opacity"
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  const videoUrl = `${window.location.origin}/watch/${video.id}`;
  const shareTitle = `Check out: ${video.video_title}`;
  const shareText = `I'm watching "${video.video_title}" on Christian Videos - Streaming Platform`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(videoUrl);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
  };

  const shareOptions = [
    {
      name: 'Facebook',
      icon: FaFacebook,
      color: 'hover:bg-blue-600',
      action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(videoUrl)}`, '_blank', 'width=600,height=400')
    },
    {
      name: 'Twitter',
      icon: FaTwitter,
      color: 'hover:bg-blue-400',
      action: () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(videoUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank', 'width=600,height=400')
    },
    {
      name: 'WhatsApp',
      icon: FaWhatsapp,
      color: 'hover:bg-green-500',
      action: () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + videoUrl)}`, '_blank')
    },
    {
      name: 'LinkedIn',
      icon: FaLinkedin,
      color: 'hover:bg-blue-700',
      action: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(videoUrl)}`, '_blank', 'width=600,height=400')
    },
    {
      name: 'Email',
      icon: FaEnvelope,
      color: 'hover:bg-gray-600',
      action: () => window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + videoUrl)}`
    }
  ];

  return (
    <div className="bg-brand-dark min-h-screen text-white">
      <SEO
        title={`${video.video_title} - Christian Videos`}
        description={`Watch ${video.video_title} on Christian Videos. ${video.video_desc || ''}`}
        url={`https://christianvideos.in/watch/${video.id}`}
      />
      {/* Back Button */}
      <div className="p-4 sm:p-6 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 hover:text-gray-300 transition-colors text-sm sm:text-base"
        >
          <FiArrowLeft size={20} className="sm:w-6 sm:h-6" />
          <span>Back</span>
        </button>
      </div>

      {/* Video Player */}
      <div className="px-4 sm:px-6 pb-4 sm:pb-6">
        <div className="relative bg-black rounded-lg overflow-hidden shadow-xl">
          <div className="w-full aspect-video bg-black">
            <iframe
              key={video.yt_id}
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${video.yt_id}?rel=0&modestbranding=1&autoplay=1&controls=1`}
              title={video.video_title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ border: 'none', display: 'block' }}
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Video Details */}
      <div className="px-4 sm:px-6 pb-12">
        {/* Title */}
        <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 leading-tight">
          {video.video_title}
        </h1>

        {/* Video Stats */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <FiThumbsUp size={16} className="sm:w-5 sm:h-5" />
            <span>{video.site_views?.toLocaleString() || 0} views</span>
          </div>
          <div className="text-xs sm:text-sm">
            Duration: {Math.floor(video.yt_length / 60)}m {video.yt_length % 60}s
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-6 sm:mb-8">
          <button className="flex items-center gap-2 px-4 sm:px-6 py-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors text-sm">
            <FiThumbsUp size={18} className="sm:w-5 sm:h-5" />
            <span>Like</span>
          </button>
          <button className="flex items-center gap-2 px-4 sm:px-6 py-2 rounded-md bg-white/20 hover:bg-white/30 transition-colors text-sm">
            <FiBookmark size={18} className="sm:w-5 sm:h-5" />
            <span>Save</span>
          </button>
          <button
            onClick={() => setShowShareModal(true)}
            className="flex items-center gap-2 px-4 sm:px-6 py-2 rounded-md bg-gradient-accent hover:opacity-90 transition-opacity text-sm font-medium"
          >
            <FiShare2 size={18} className="sm:w-5 sm:h-5" />
            <span>Share</span>
          </button>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-6 sm:p-8 w-full max-w-md shadow-2xl border border-gray-700">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Share Video</h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Social Share Buttons */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {shareOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.name}
                    onClick={option.action}
                    className={`flex flex-col items-center justify-center gap-2 p-3 rounded-lg bg-gray-800 text-white transition-all ${option.color} transform hover:scale-105`}
                    title={`Share on ${option.name}`}
                  >
                    <IconComponent size={20} />
                    <span className="text-xs font-medium text-center">{option.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Copy Link Section */}
            <div className="border-t border-gray-700 pt-6">
              <p className="text-sm text-gray-400 mb-3">Or copy link</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={videoUrl}
                  readOnly
                  className="flex-1 bg-gray-800 text-white text-sm px-3 py-2 rounded-lg border border-gray-700 focus:outline-none"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${copyFeedback
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-800 text-white hover:bg-gray-700'
                    }`}
                >
                  <FiCopy size={16} />
                  <span className="text-sm">{copyFeedback ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Watch;
